import React, { useMemo } from 'react';
import {
    InfiniteData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { objectTotal } from '@travelity/utils';
import {
    compareTimeOffset,
    convertOptionToOptionDto,
    durationToDurationDto,
    getProductDtoToProduct,
    getProductReferenceToProductMinimal,
    pricingToPricingDto,
    productCapacityToCapacityDto,
    routeStopsToStopsDto,
} from './product.converters';
import {
    type CreateProductReq1Dto,
    type CreateProductReqDto as CreateProductReq0Dto,
    type GetProductRes1Dto,
    type GetProductResDto as GetProductRes0Dto,
    GetProductsResDto,
    GetProductsResItem0Dto,
    GetProductsResItem1Dto,
    PaxData,
    type ProductReferenceProjectionList,
    ProductsService,
    ProductType,
    UpdateProductConfigurationReqDto,
    UpdateProductPoliciesReqDto,
    UpdateProductReq1Dto,
    UpdateProductReqDto as UpdateProductReq0Dto,
    UpdateProductRoutesReqDistanceDto,
    type CreateProductReq2Dto,
    type GetProductsResItem2Dto,
    type UpdateProductReq2Dto,
    type UpdatePackageDiscountReqDto,
} from '../../generated';
import {
    CustomInfiniteQueryOptions,
    CustomMutationOptions,
    CustomQueryOptions,
    DurationValue,
} from '../common.types';
import {
    Option,
    Package,
    Product,
    ProductCapacityItem,
    ProductFinancial,
    ProductMinimal,
    type ProductRoute,
    ProductScheduleItem,
    type RouteDistance,
    RouteItem,
} from './product.types';
import {
    convertAttachmentToAttachmentDto,
    convertItemsToActionItemsDto,
} from '../common.converters';
import { ScheduleItem } from '../schedule/schedule.types';
import { productScheduleToScheduleDto } from '../schedule/schedule.converters';
import { getCapacityDtoToCapacity } from '../capacity/capacity.converters';
import { CapacityItem } from '../capacity/capacity.types';
import { Attachment } from '../customer/customer.types';

export const useProductsLazyKey = 'useProductsLazyKey';
export const useProductsKey = 'useProductsKey';
export const useProductKey = 'useProductKey';

const mergeItems = <T extends Product | Package>(item: T, newItem: T): T => ({
    ...item,
    ...newItem,
    type: newItem.type || item.type,
});

type GetProductResDto = GetProductRes0Dto | GetProductRes1Dto;
type CreateProductReqDto =
    | CreateProductReq0Dto
    | CreateProductReq1Dto
    | CreateProductReq2Dto;

export const useProductCache = () => {
    const queryClient = useQueryClient();

    const update = (id: string, newItem: Product) => {
        queryClient
            .getQueriesData({
                queryKey: [useProductsLazyKey],
                exact: false,
            })
            .forEach(([queryKey]) => {
                queryClient.setQueryData<InfiniteData<ProductsResponse>>(
                    queryKey,
                    data => {
                        if (!data?.pages) return undefined;
                        const pages = data.pages.map(page => {
                            return {
                                ...page,
                                items:
                                    page.items.map(item =>
                                        item.id === id
                                            ? mergeItems(item, newItem)
                                            : item
                                    ) || [],
                            };
                        });
                        return { ...data, pages };
                    }
                );
            });
        queryClient
            .getQueriesData({
                queryKey: [useProductKey, id],
                exact: true,
            })
            .forEach(([queryKey]) => {
                queryClient.setQueryData<Product>(queryKey, data => {
                    return data ? mergeItems(data, newItem) : undefined;
                });
            });
    };

    return {
        update,
    };
};

export const useGetProductById = (id?: string) => {
    return useQuery({
        queryKey: [useProductKey, id],
        queryFn: async () => {
            const response = await ProductsService.getProduct({
                path: { product_id: id! },
            });

            if (!response.data) throw new Error('Product not found');

            return response.data.type === 'dynamic_package'
                ? response.data
                : getProductDtoToProduct(response.data);
        },
        enabled: !!id,
    });
};

const useProductCapacitiesKey = 'useProductCapacitiesKey';
export const useProductCapacities = (
    id?: string,
    pax?: PaxData,
    options: CustomQueryOptions<CapacityItem[]> = {}
) => {
    return useQuery({
        queryKey: [useProductCapacitiesKey, id, pax],
        queryFn: async () => {
            const response = await ProductsService.getProductFittingCapacities({
                path: { product_id: id as string },
                body: { ...(pax as PaxData) },
            });
            return response.data?.items?.map(getCapacityDtoToCapacity) || [];
        },
        enabled: !!id && !!pax && !!objectTotal(pax),
        ...options,
    });
};

export const useGenerateProductName = (
    options: CustomQueryOptions<any> = {}
) => {
    return useQuery({
        queryKey: [],
        queryFn: async () => {
            const response = await ProductsService.generateProductName();
            return response.data;
        },
        cacheTime: 0,
        ...options,
    });
};

export interface UseProductsParams {
    types?: ProductType[];
}

interface UseDebouncedProductsLazyProps {
    search: string;
    types?: ProductType[];
    pageSize: number;
    referencesOnly?: boolean;
    resolveSchedule?: boolean;
    availableFromTs?: number;
    options?: CustomInfiniteQueryOptions<ProductReferenceProjectionList> & {
        onInvalidPagination?: () => void;
    };
}
const useDebouncedProductsLazyKey = 'useDebouncedProductsLazyKey';
export const useDebouncedProductsLazy = ({
    search,
    types,
    pageSize = 10,
    referencesOnly = true,
    resolveSchedule = false,
    availableFromTs,
    options,
}: UseDebouncedProductsLazyProps) => {
    const [debouncedParams, setDebouncedParams] = React.useState<
        UseProductsData | undefined
    >();

    React.useEffect(() => {
        const handler = setTimeout(() => {
            const filter: UseProductsData = {};
            if (search.length > 2) {
                filter.text_search = search;
            }
            setDebouncedParams(Object.keys(filter).length ? filter : undefined);
        }, 1000);

        return () => {
            clearTimeout(handler);
        };
    }, [search]);

    const { data, ...other } = useInfiniteQuery({
        queryKey: [
            useDebouncedProductsLazyKey,
            debouncedParams,
            availableFromTs,
            types,
            pageSize,
        ],
        queryFn: async ({ pageParam }) => {
            const result = (await ProductsService.getProducts({
                query: {
                    page_size: pageSize,
                    page_number: pageParam?.page || 0,
                    start_at_id: pageParam?.startAtId || undefined,
                    references_only: referencesOnly,
                    resolve_schedule: resolveSchedule,
                    ...(availableFromTs && {
                        available_from_ts: availableFromTs,
                    }),
                    ...(types?.length
                        ? { types }
                        : { types: [ProductType.TRANSFER, ProductType.TOUR] }),
                    ...(debouncedParams?.text_search && {
                        text_search: debouncedParams.text_search,
                    }),
                },
            })) as { data: GetProductsResDto };

            return {
                total_count: result.data.total_count,
                items:
                    (result.data.items as (
                        | GetProductsResItem0Dto
                        | GetProductsResItem1Dto
                    )[]) || [],
                next_id: result.data.next_id,
                next_page_number: result.data.next_page_number,
            };
        },
        getNextPageParam: lastPage => {
            if (!lastPage.items || !lastPage.next_id) return undefined;
            return {
                startAtId: lastPage.next_id,
                page: lastPage.next_page_number,
            };
        },
        enabled: !!debouncedParams,
        keepPreviousData: false,
        staleTime: 0,
        ...options,
        onError: (error: any) => {
            if (error?.status === 409) {
                options?.onInvalidPagination?.();
            } else options?.onError?.(error);
        },
    });

    const parsedData = useMemo(
        () =>
            data?.pages
                ? data.pages
                      .map(
                          page =>
                              page.items?.map(
                                  getProductReferenceToProductMinimal
                              ) || []
                      )
                      .reduce((arr, cur) => [...arr, ...cur], [])
                : undefined,
        [data]
    );

    return {
        data: parsedData,
        ...other,
        isLoading: other.isFetching,
    };
};

export const useProducts = (
    params: UseProductsParams = {},
    options: CustomQueryOptions<any> = {}
) => {
    const { data, ...rest } = useQuery({
        queryKey: [useProductsKey],
        queryFn: async () => {
            return ProductsService.getProducts1({
                query: { page_size: 1000 },
            });
        },
        staleTime: 30 * 60 * 1000,
        cacheTime: 5 * 60 * 1000,
        ...options,
    });

    const parsedData: ProductMinimal[] | undefined = useMemo(
        () =>
            data?.items
                .filter(
                    (p: any) =>
                        !params.types?.length || params.types.includes(p.type)
                )
                .map(getProductReferenceToProductMinimal),
        [data, params]
    );

    return {
        data: parsedData,
        ...rest,
    };
};

// Get all products lazy

export interface UseProductsData {
    type?: ProductType;
    name?: string;
    startTs?: number;
    endTs?: number;
    currency?: string;
    minPrice?: number;
    maxPrice?: number;
    at_start?: number;
    at_end?: number;
    by?: string;
    action?: 'created' | 'last_updated' | 'deleted';
    text_search?: string;
    groupId?: Array<string>;
    pageNumber?: number;
    pageSize?: number;
}

type UseProductsLazyData = UseProductsData;

interface ProductsResponse {
    items: (Product | Package)[];
    next_id?: string;
    total_count?: number;
    next_page_number?: number;
}

export const useProductsLazy = (
    params: UseProductsLazyData = {},
    options: CustomInfiniteQueryOptions<ProductsResponse> & {
        onInvalidPagination?: () => void;
    } = {}
) => {
    const pageSize = params.pageSize || 5;

    const { data, ...other } = useInfiniteQuery({
        queryKey: [useProductsLazyKey, params],
        queryFn: async ({ pageParam }) => {
            const response = await ProductsService.getProducts({
                query: {
                    ...(params.action && { action: params.action }),
                    ...(params.at_start && { at_start: params.at_start }),
                    ...(params.at_end && { at_end: params.at_end }),
                    ...(params.by && { by: params.by }),
                    ...(params.type && { types: [params.type] }),
                    ...(params.text_search && {
                        text_search: params.text_search,
                    }),
                    page_number: pageParam?.page || 0,
                    page_size: pageSize,
                    ...(pageParam?.startAtId && {
                        start_at_id: pageParam?.startAtId,
                    }),
                },
            });

            if (!response.data) throw new Error('Failed to get products');
            const result = response.data as GetProductsResDto;

            return {
                total_count: result.total_count,
                items:
                    result.items?.map(item =>
                        item.type === 'dynamic_package'
                            ? item
                            : getProductDtoToProduct(item)
                    ) || [],
                next_id: result.next_id,
                next_page_number: result.next_page_number,
            };
        },
        getNextPageParam: lastPage => {
            if (!lastPage.items || !lastPage.next_id) return undefined;
            return {
                startAtId: lastPage.next_id,
                page: lastPage.next_page_number,
            };
        },
        ...options,
        onError: (error: any) => {
            if (error?.status === 409) {
                options.onInvalidPagination?.();
            } else options.onError?.(error);
        },
    });

    const totalCount = data?.pages.find(page => page.total_count)?.total_count;
    const parsedData = useMemo(
        () =>
            data?.pages
                ? data.pages
                      .map(page => page.items)
                      .reduce((arr, cur) => [...arr, ...cur], [])
                : undefined,
        [data]
    );

    return {
        totalCount,
        data: parsedData as GetProductsResItem2Dto[] | Product[],
        ...other,
    };
};

// Add Product

export const useAddProduct = (
    options?: CustomMutationOptions<CreateProductReqDto, Product>
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async data => {
            const response = await ProductsService.createProduct({
                body: data,
            });

            if (!response.data) throw new Error('Product not found');

            return getProductDtoToProduct(response.data as GetProductResDto);
        },
        ...options,
        onSuccess: (...args) => {
            queryClient.invalidateQueries({
                queryKey: [useProductsLazyKey],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: [useProductsKey],
                exact: false,
            });
            options?.onSuccess?.(...args);
        },
    });
};

export const useDeleteProduct = (
    options?: CustomMutationOptions<
        {
            productId: string;
        },
        Product
    >
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ productId }) => {
            const response = await ProductsService.deleteProduct({
                path: { product_id: productId },
            });

            if (!response.data) throw new Error('Product not found');
            return getProductDtoToProduct(response.data as GetProductResDto);
        },
        ...options,
        onSuccess: (...args) => {
            queryClient.invalidateQueries({
                queryKey: [useProductsLazyKey],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: [useProductsKey],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: [useProductKey, args[1].productId],
                exact: true,
            });
            options?.onSuccess?.(...args);
        },
    });
};

// Update Product
export const useUpdateProductConfiguration = (
    options?: CustomMutationOptions<
        { productId: string; data: UpdateProductConfigurationReqDto },
        Product
    >
) => {
    const { update } = useProductCache();

    return useMutation({
        mutationFn: async ({ productId, data }) => {
            const response = await ProductsService.updateProductConfiguration({
                path: { product_id: productId },

                body: data,
            });

            if (!response.data) throw new Error('Product not found');
            return getProductDtoToProduct(response.data as GetProductResDto);
        },
        ...options,
        onSuccess: (...args) => {
            update(args[1].productId, args[0]);
            options?.onSuccess?.(...args);
        },
        onError: (error, ...args) => {
            // @ts-ignore
            if (error?.status === 409) {
                options?.onSuccess?.({} as Product, ...args);
            } else {
                options?.onError?.(error, ...args);
            }
        },
    });
};

export const useUpdateProductDetails = (
    options?: CustomMutationOptions<
        {
            productId: string;
            data:
                | UpdateProductReq0Dto
                | UpdateProductReq1Dto
                | UpdateProductReq2Dto;
        },
        Product
    >
) => {
    const { update } = useProductCache();

    return useMutation({
        mutationFn: async ({ productId, data }) => {
            const response = await ProductsService.updateProduct({
                path: { product_id: productId },

                body: data,
            });

            if (!response.data) throw new Error('Product not found');
            return getProductDtoToProduct(response.data as GetProductResDto);
        },
        ...options,
        onSuccess: (...args) => {
            update(args[1].productId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateProductPolicies = (
    options?: CustomMutationOptions<
        {
            productId: string;
            data: UpdateProductPoliciesReqDto;
        },
        Product
    >
) => {
    const { update } = useProductCache();

    return useMutation({
        mutationFn: async ({ productId, data }) => {
            const response = await ProductsService.updateProductPolicies({
                path: { product_id: productId },
                body: data,
            });

            if (!response.data) throw new Error('Product not found');
            return getProductDtoToProduct(response.data as GetProductResDto);
        },
        ...options,
        onSuccess: (...args) => {
            update(args[1].productId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateProductRoute = (
    oldItems: ProductRoute,
    options?: CustomMutationOptions<
        {
            productId: string;
            routeItems: RouteItem[];
            newDistance: RouteDistance;
        },
        Product | null
    >
) => {
    const { update } = useProductCache();

    return useMutation({
        mutationFn: async ({ productId, routeItems, newDistance }) => {
            const requestItems = convertItemsToActionItemsDto(
                routeStopsToStopsDto(oldItems.stops),
                routeStopsToStopsDto(routeItems),
                'id',
                (oldItem, newItem) =>
                    oldItem.location?.name !== newItem.location?.name ||
                    !compareTimeOffset(
                        oldItem.schedule?.stop_duration,
                        newItem.schedule?.stop_duration
                    ) ||
                    !compareTimeOffset(
                        oldItem.schedule?.arrival_offset,
                        newItem.schedule?.arrival_offset
                    )
            );

            const isDistanceChanged = !isEqual(oldItems.distance, newDistance);
            const distanceConverted = {
                [newDistance.type]: newDistance.value,
            } as UpdateProductRoutesReqDistanceDto;
            const oldDistanceConverted = {
                [oldItems.distance.type]: oldItems.distance.value,
            } as UpdateProductRoutesReqDistanceDto;

            if (!requestItems.length && isDistanceChanged) {
                const response = await ProductsService.updateProductRoutes({
                    path: { product_id: productId },
                    body: {
                        distance: distanceConverted,
                    },
                });

                if (!response.data) throw new Error('Product not found');
                return getProductDtoToProduct(
                    response.data as GetProductResDto
                );
            }

            if (requestItems.length) {
                const response = await ProductsService.updateProductRoutes({
                    path: { product_id: productId },
                    body: {
                        round_trip: !!routeItems[0]?.repeatLocation,
                        items: requestItems,
                        distance: isDistanceChanged
                            ? distanceConverted
                            : oldDistanceConverted,
                    },
                });

                if (!response.data) throw new Error('Product not found');
                return getProductDtoToProduct(
                    response.data as GetProductResDto
                );
            }
            return null;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].productId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateProductCapacities = (
    oldItems: ProductCapacityItem[],
    options: CustomMutationOptions<
        {
            productId: string;
            items: ProductCapacityItem[];
        },
        Product | null
    > = {}
) => {
    const { update } = useProductCache();

    return useMutation({
        mutationFn: async ({ productId, items }) => {
            const requestItems = convertItemsToActionItemsDto(
                oldItems.map(productCapacityToCapacityDto),
                items.map(productCapacityToCapacityDto),
                'reference.id'
            );

            if (requestItems.length) {
                const response = await ProductsService.updateProductCapacities({
                    path: { product_id: productId },

                    body: { items: requestItems },
                });

                if (!response.data) throw new Error('Product not found');
                return getProductDtoToProduct(
                    response.data as GetProductResDto
                );
            }
            return null;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].productId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateProductSchedules = (
    oldSchedules: {
        items: ProductScheduleItem[];
        duration?: DurationValue;
        swing?: string;
    },
    options: CustomMutationOptions<
        {
            items: (ProductScheduleItem | ScheduleItem)[];
            duration?: DurationValue;
            swing?: string;
            productId: string;
        },
        Product | null
    > = {}
) => {
    const { update } = useProductCache();
    const {
        items: oldItems,
        duration: oldDurationValue,
        swing: oldSwing,
    } = oldSchedules;

    return useMutation({
        mutationFn: async ({ productId, items, duration, swing }) => {
            const oldDuration = durationToDurationDto(
                oldDurationValue,
                oldSwing
            );
            const newDuration = durationToDurationDto(duration, swing);
            const requestItems = convertItemsToActionItemsDto(
                oldItems.map(s => productScheduleToScheduleDto(s, oldDuration)),
                // @ts-ignore
                items.map(s => productScheduleToScheduleDto(s, newDuration)),
                'reference.id',
                (oldItem, newItem) =>
                    !compareTimeOffset(
                        oldItem.duration?.estimated,
                        newItem.duration?.estimated
                    ) ||
                    !compareTimeOffset(
                        oldItem.duration?.deviation,
                        newItem.duration?.deviation
                    )
            );

            if (requestItems.length) {
                const response = await ProductsService.updateProductSchedule({
                    path: { product_id: productId },

                    body: { items: requestItems },
                });

                if (!response.data) throw new Error('Product not found');
                return getProductDtoToProduct(
                    response.data as GetProductResDto
                );
            }
            return null;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].productId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateProductPricing = (
    oldItems?: ProductFinancial,
    options: CustomMutationOptions<
        {
            productId: string;
            data: ProductFinancial;
        },
        Product | null
    > = {}
) => {
    const { update } = useProductCache();

    return useMutation({
        mutationFn: async ({ productId, data: { items, ...financial } }) => {
            const requestItems = convertItemsToActionItemsDto(
                oldItems?.items.map(pricingToPricingDto) || [],
                items.map(pricingToPricingDto),
                'id',
                (oldItem, newItem) =>
                    JSON.stringify(oldItem) !== JSON.stringify(newItem)
            );

            if (requestItems.length) {
                const response = await ProductsService.updateProductPricing({
                    path: { product_id: productId },

                    body: {
                        payment_requirements: {
                            methods: financial.paymentMethods,
                            prepayment: {
                                required:
                                    !!financial.prepaymentAmount &&
                                    !!financial.prepayment,
                                type: financial.prepaymentType,
                                amount: financial.prepaymentAmount,
                            },
                        },
                        items: requestItems,
                    },
                });

                if (!response.data) throw new Error('Product not found');
                return getProductDtoToProduct(
                    response.data as GetProductResDto
                );
            }
            return null;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].productId, args[0]);
            options?.onSuccess?.(...args);
        },
        onError: (error, ...args) => {
            options?.onError?.(error, ...args);
        },
    });
};

export const useUpdateProductOptions = (
    oldItems: Option[],
    options: CustomMutationOptions<
        {
            productId: string;
            items: Option[];
        },
        Product | null
    > = {}
) => {
    const { update } = useProductCache();

    return useMutation({
        mutationFn: async ({ productId, items }) => {
            const requestItems = convertItemsToActionItemsDto(
                oldItems.map(convertOptionToOptionDto),
                items.map(convertOptionToOptionDto),
                'id',
                (oldItem, newItem) =>
                    JSON.stringify(oldItem) !== JSON.stringify(newItem)
            );

            if (requestItems.length) {
                const response = await ProductsService.updateProductOptions({
                    path: { product_id: productId },

                    body: { items: requestItems },
                });

                if (!response.data) throw new Error('Product not found');
                return getProductDtoToProduct(
                    response.data as GetProductResDto
                );
            }
            return null;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].productId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useShareProduct = (
    options?: CustomMutationOptions<
        {
            productId: string;
        },
        Product | null
    >
) => {
    return useMutation({
        mutationFn: async () => {
            return null;
        },
        ...options,
    });
};

export const useUpdateProductImages = (
    oldItems: Attachment[],
    options?: CustomMutationOptions<
        {
            productId: string;
            items: Attachment[];
        },
        Product | null
    >
) => {
    const { update } = useProductCache();

    return useMutation({
        mutationFn: async ({
            productId,
            items,
        }: {
            productId: string;
            items: Attachment[];
        }) => {
            const requestItems = convertItemsToActionItemsDto(
                oldItems.map(convertAttachmentToAttachmentDto),
                items.map(convertAttachmentToAttachmentDto),
                'id',
                (oldItem, newItem) =>
                    JSON.stringify(oldItem) !== JSON.stringify(newItem)
            );

            if (requestItems.length) {
                const response = await ProductsService.updateProductAttachments(
                    {
                        path: { product_id: productId },

                        body: { items: requestItems },
                    }
                );

                if (!response.data) throw new Error('Product not found');
                return getProductDtoToProduct(
                    response.data as GetProductResDto
                );
            }
            return null;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].productId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export type UpdatePackageDiscountInput = {
    productId: string;
    data: UpdatePackageDiscountReqDto;
};

export const useUpdatePackageDiscount = () => {
    return useMutation({
        mutationFn: async ({ productId, data }: UpdatePackageDiscountInput) => {
            const response = await ProductsService.updatePackageDiscount({
                path: {
                    product_id: productId,
                },
                body: data,
            });

            return response.data;
        },
    });
};
